<template>
  <v-container class="py-6" fluid>
    <div class="d-flex align-center mb-6">
      <v-avatar color="primary lighten-5" size="52" class="mr-4">
        <v-icon color="primary">mdi-calendar-remove</v-icon>
      </v-avatar>
      <div>
        <h1 class="text-h5 font-weight-bold primary--text mb-1">Reporte de inasistencias</h1>
        <div class="text-body-2 grey--text text--darken-1">
          Consulta las inasistencias de un estudiante y descarga su reporte.
        </div>
      </div>
    </div>

    <v-card class="rounded-xl elevation-4 mb-6">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon left color="primary">mdi-filter-variant</v-icon>
        Parámetros de consulta
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-6">
        <v-alert v-if="studentsError" type="error" dense text class="mb-5">
          {{ studentsError }}
        </v-alert>

        <v-row align="center">
          <v-col cols="12" md="5">
            <v-autocomplete
              v-model="selectedStudentId"
              :items="students"
              :item-text="studentLabel"
              :filter="filterStudent"
              item-value="id"
              :loading="loadingStudents"
              :disabled="loadingStudents || loadingReport"
              label="Estudiante"
              placeholder="Busca por nombre o apellido"
              prepend-inner-icon="mdi-account-search"
              outlined
              dense
              clearable
              hide-details="auto"
              no-data-text="No se encontraron estudiantes"
              @change="clearReport"
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar color="primary lighten-5">
                  <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                  <v-icon v-else color="primary">mdi-account</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ studentLabel(item) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.classroom || 'Sin salón asignado' }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="startDate"
              label="Fecha inicial"
              type="date"
              :max="today"
              :disabled="loadingReport"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-calendar-start"
              @change="clearReport"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="endDate"
              label="Fecha final"
              type="date"
              :min="startDate"
              :max="today"
              :disabled="loadingReport"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-calendar-end"
              @change="clearReport"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              block
              depressed
              :loading="loadingReport"
              :disabled="!canSearch"
              @click="getAbsences"
            >
              <v-icon left>mdi-magnify</v-icon>
              Consultar
            </v-btn>
          </v-col>
        </v-row>

        <div class="text-caption grey--text text--darken-1 mt-4">
          Selecciona un estudiante y un periodo. La consulta incluye las fechas inicial y final. Solamente se consideran las inasisencias de Lunes a Viernes.
        </div>
      </v-card-text>
    </v-card>

    <v-alert v-if="reportError" type="error" dense text class="mb-6">
      {{ reportError }}
    </v-alert>

    <v-card v-if="reportGenerated" class="rounded-xl elevation-4 overflow-hidden">
      <v-card-title class="d-flex flex-wrap align-center">
        <div>
          <div class="text-subtitle-1 font-weight-bold">Resultado de la consulta</div>
          <div class="text-body-2 grey--text text--darken-1">
            {{ selectedStudent ? studentLabel(selectedStudent) : '' }}
            <span v-if="selectedStudent && selectedStudent.classroom"> · {{ selectedStudent.classroom }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-chip color="primary lighten-5" text-color="primary" class="font-weight-bold">
          {{ absences.length }} {{ absences.length === 1 ? 'inasistencia' : 'inasistencias' }}
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>
      <v-card-text class="d-flex flex-wrap align-center">
        <div class="text-body-2 grey--text text--darken-1">
          Periodo: <strong>{{ formatDate(startDate) }} - {{ formatDate(endDate) }}</strong>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="red darken-1"
          outlined
          class="mr-2 mb-2"
          @click="downloadPdf"
        >
          <v-icon left>mdi-file-pdf-box</v-icon>
          Descargar PDF
        </v-btn>
        <vue-excel-xlsx
          :data="excelRows"
          :columns="excelColumns"
          :file-name="excelFileName"
          sheet-name="Inasistencias"
        >
          <v-btn color="success" outlined class="mb-2">
            <v-icon left>mdi-file-excel</v-icon>
            Descargar Excel
          </v-btn>
        </vue-excel-xlsx>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="absenceRows"
        :items-per-page="10"
        class="absence-table"
        no-data-text="No se encontraron inasistencias en este periodo."
        :footer-props="{
          'items-per-page-text': 'Filas por página',
          'items-per-page-options': [5, 10, 20]
        }"
      >
        <template v-slot:item.formattedDate="{ item }">
          <div class="d-flex align-center">
            <v-icon small color="error" class="mr-2">mdi-calendar-alert</v-icon>
            {{ item.formattedDate }}
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-card v-else-if="!loadingReport && !reportError" flat class="rounded-xl text-center pa-8 empty-state">
      <v-icon size="54" color="grey lighten-1">mdi-clipboard-text-search-outline</v-icon>
      <div class="text-subtitle-1 font-weight-medium mt-3">Tu reporte aparecerá aquí</div>
      <div class="text-body-2 grey--text text--darken-1 mt-1">
        Elige un estudiante y las fechas que deseas consultar.
      </div>
    </v-card>
  </v-container>
</template>

<script>
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { HTTP } from '@/main.js'
import store from '@/store'

function toISODate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'StudentAbsences',

  data() {
    const today = toISODate(new Date())
    return {
      today,
      startDate: `${today.slice(0, 7)}-01`,
      endDate: today,
      students: [],
      selectedStudentId: null,
      absences: [],
      loadingStudents: false,
      loadingReport: false,
      reportGenerated: false,
      studentsError: '',
      reportError: ''
    }
  },

  computed: {
    selectedStudent() {
      return this.students.find(student => student.id === this.selectedStudentId) || null
    },
    canSearch() {
      return Boolean(
        this.selectedStudentId &&
        this.startDate &&
        this.endDate &&
        this.startDate <= this.endDate &&
        this.endDate <= this.today
      )
    },
    headers() {
      return [
        { text: 'Fecha de inasistencia', value: 'formattedDate', align: 'start' }
      ]
    },
    absenceRows() {
      return this.absences.map(date => ({
        date,
        formattedDate: this.formatDate(date)
      }))
    },
    excelColumns() {
      return [
        { label: 'Fecha de inasistencia', field: 'formattedDate' }
      ]
    },
    excelRows() {
      return this.absenceRows.length
        ? this.absenceRows
        : [{ formattedDate: 'Sin inasistencias en el periodo' }]
    },
    excelFileName() {
      const studentName = this.selectedStudent
        ? this.fileSafeName(this.studentLabel(this.selectedStudent))
        : 'estudiante'
      return `inasistencias_${studentName}_${this.startDate}_${this.endDate}`
    }
  },

  created() {
    this.init()
  },

  methods: {
    async init() {
      this.loadingStudents = true;
      try {
        await Promise.all([
          this.getStudents()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        this.loadingStudents = false;
      }
    },
    studentLabel(student) {
      return `${student.first_name || ''} ${student.last_name || ''}`.trim()
    },

    normalizeStudentSearch(value) {
      return value
        ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        : ''
    },

    filterStudent(student, queryText) {
      const query = this.normalizeStudentSearch(queryText)
      const fullName = this.normalizeStudentSearch(this.studentLabel(student))
      return fullName.includes(query)
    },

    async getStudents() {
      this.loadingStudents = true
      this.studentsError = ''
      try {
        const response = await HTTP.get('student/', {
          headers: { Authorization: `Token ${store.getters.getToken}` }
        })
        if (!Array.isArray(response.data)) {
          throw new Error('La respuesta de estudiantes no tiene el formato esperado.')
        }
        this.students = response.data.slice().sort((a, b) =>
          this.studentLabel(a).localeCompare(this.studentLabel(b), 'es')
        )
      } catch (error) {
        this.studentsError = this.getRequestError(error, 'No fue posible cargar los estudiantes.')
      } finally {
        this.loadingStudents = false
      }
    },

    async getAbsences() {
      if (!this.canSearch) return
      this.loadingReport = true
      this.reportError = ''
      this.reportGenerated = false
      this.absences = []

      const [startYear, startMonth, startDay] = this.startDate.split('-')
      const [endYear, endMonth, endDay] = this.endDate.split('-')
      const path = `student/${this.selectedStudentId}/absences/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}/`

      try {
        const response = await HTTP.get(path, {
          headers: { Authorization: `Token ${store.getters.getToken}` }
        })
        if (!Array.isArray(response.data) || response.data.some(date =>
          typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)
        )) {
          throw new Error('La respuesta de inasistencias no tiene el formato esperado.')
        }
        this.absences = response.data
        this.reportGenerated = true
      } catch (error) {
        this.reportError = this.getRequestError(error, 'No fue posible consultar las inasistencias.')
      } finally {
        this.loadingReport = false
      }
    },

    clearReport() {
      this.absences = []
      this.reportGenerated = false
      this.reportError = ''
    },

    formatDate(date) {
      if (!date) return ''
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },

    getRequestError(error, fallback) {
      const status = error.response && error.response.status
      const messages = {
        400: 'El periodo seleccionado no es válido. Revisa las fechas.',
        401: 'El servidor rechazó tu token. Verifica que hayas iniciado sesión en el mismo entorno del servidor y vuelve a iniciar sesión.',
        403: 'No tienes permisos de administrador para consultar este reporte.',
        404: 'No se encontró el estudiante o no pertenece a tu escuela.'
      }
      if (status && messages[status]) return messages[status]
      if (error.message) return `${fallback} ${error.message}`
      return fallback
    },

    fileSafeName(value) {
      return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9_-]+/gi, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase()
    },

    downloadPdf() {
      if (!this.reportGenerated || !this.selectedStudent) return

      const doc = new jsPDF('portrait', 'pt', 'letter')
      const studentName = this.studentLabel(this.selectedStudent)
      doc.setFontSize(18)
      doc.text('Reporte de inasistencias', 40, 48)
      doc.setFontSize(11)
      doc.text(`Estudiante: ${studentName}`, 40, 72)
      doc.text(`Salón: ${this.selectedStudent.classroom || 'Sin salón asignado'}`, 40, 90)
      doc.text(`Periodo: ${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)}`, 40, 108)
      doc.text(`Total de inasistencias: ${this.absences.length}`, 40, 126)
      doc.autoTable({
        startY: 144,
        head: [['Fecha de inasistencia']],
        body: this.absenceRows.length
          ? this.absenceRows.map(row => [row.formattedDate])
          : [['Sin inasistencias en el periodo']],
        theme: 'striped',
        headStyles: { fillColor: [39, 93, 145] },
        margin: { bottom: 40 },
        didDrawPage: () => {
          const pageSize = doc.internal.pageSize
          const pageHeight = pageSize.height || pageSize.getHeight()
          const pageWidth = pageSize.width || pageSize.getWidth()
          doc.setFontSize(9)
          doc.setTextColor(120)
          doc.text('Powered by Intrare', pageWidth / 2, pageHeight - 18, { align: 'center' })
        }
      })
      doc.save(`${this.excelFileName}.pdf`)
    }
  }
}
</script>

<style scoped>
.empty-state {
  background: #f7f9fc;
}

.absence-table >>> th {
  font-weight: 700;
}
</style>